/**
 * Challenge — public detail page for a community Challenge.
 *
 * iter-72 (e): /challenge/:slug
 *   • Header: title + prompt + theme hint + submission count
 *   • Primary CTA: "write your version" — preloads the prompt into Home's
 *     feeling field via query param (`?prompt=...&challenge=<slug>`).
 *   • Gallery: most-recent public submissions (slim payload from backend).
 *
 * Free-of-charge feature — no paywall here. Submissions only show cards
 * with public slugs (i.e. shareable), so private cards stay private.
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Sparkles, Calendar, Users } from "lucide-react";
import { getChallenge } from "../lib/api";
import { track } from "../lib/track";

function fmtEndsAt(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const now = new Date();
        const days = Math.round((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
        if (days < 0) return "ended";
        if (days === 0) return "ends today";
        if (days === 1) return "ends tomorrow";
        if (days < 14) return `ends in ${days} days`;
        return `ends ${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
    } catch {
        return null;
    }
}

export default function Challenge() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getChallenge(slug)
            .then((d) => { if (alive) { setData(d); track("challenge_view", { slug }); } })
            .catch(() => { if (alive) setErr("not found"); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [slug]);

    const ch = data?.challenge;
    const subs = data?.submissions || [];
    const ended = !ch?.active;

    if (loading) {
        return (
            <div className="min-h-screen bg-app text-app flex items-center justify-center">
                <span className="font-serif italic text-app-3">loading challenge…</span>
            </div>
        );
    }
    if (err || !ch) {
        return (
            <div className="min-h-screen bg-app text-app flex flex-col items-center justify-center gap-3 px-4">
                <span className="font-serif italic text-app-3 text-lg">challenge not found</span>
                <Link to="/" className="text-amber-300/85 hover:text-amber-200 underline underline-offset-2 text-sm" data-testid="challenge-back-home">back home</Link>
            </div>
        );
    }

    const writeUrl = `/?prompt=${encodeURIComponent(ch.prompt)}&challenge=${encodeURIComponent(ch.slug)}`;

    return (
        <div className="min-h-screen bg-app text-app pb-16">
            <Helmet>
                <title>{`#${ch.slug} — ${ch.title} · MoodCard`}</title>
                <meta name="description" content={ch.prompt} />
            </Helmet>

            {/* Back nav */}
            <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1.5 text-app-3 hover:text-app-2 text-sm transition-colors"
                    data-testid="challenge-back"
                >
                    <ArrowLeft className="w-4 h-4" />
                    <span>back</span>
                </button>
            </div>

            {/* Header — chunky, asymmetric, sets the campaign tone */}
            <header className="max-w-3xl mx-auto px-4 sm:px-6 mt-8 sm:mt-12">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-amber-300/80 mb-3">
                    <Sparkles className="w-3 h-3" />
                    <span>community challenge</span>
                    {ended && <span className="px-2 py-0.5 rounded-full bg-app-3/10 text-app-3 normal-case tracking-normal italic font-serif text-[11px]">ended</span>}
                </div>
                <h1 className="text-3xl sm:text-5xl font-display text-app leading-tight">
                    {ch.title}
                </h1>
                <p className="mt-4 sm:mt-6 text-lg sm:text-xl font-serif italic text-app-2 leading-snug max-w-2xl">
                    "{ch.prompt}"
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-app-3">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-1 border border-app/10">
                        <Users className="w-3 h-3" />
                        <span><span className="text-app-2 font-medium">{data?.submission_count ?? subs.length}</span> moodcards</span>
                    </div>
                    {ch.theme && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-1 border border-app/10">
                            <span className="text-app-3">theme</span>
                            <span className="text-app-2 font-medium italic font-serif normal-case">{ch.theme}</span>
                        </div>
                    )}
                    {fmtEndsAt(ch.ends_at) && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-1 border border-app/10">
                            <Calendar className="w-3 h-3" />
                            <span className="italic font-serif normal-case">{fmtEndsAt(ch.ends_at)}</span>
                        </div>
                    )}
                </div>

                {!ended && (
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => { track("challenge_write_clicked", { slug: ch.slug }); navigate(writeUrl); }}
                        className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors"
                        data-testid="challenge-write-cta"
                    >
                        write your version
                        <span aria-hidden>→</span>
                    </motion.button>
                )}
            </header>

            {/* Gallery */}
            <section className="max-w-5xl mx-auto px-4 sm:px-6 mt-14 sm:mt-20">
                <div className="text-[10px] tracking-[0.22em] uppercase text-app-3 mb-4">
                    submissions
                </div>
                {subs.length === 0 ? (
                    <p className="text-app-3 italic font-serif">be the first to write your version.</p>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {subs.map((s) => (
                            <Link
                                key={s.id}
                                to={`/c/${s.slug}`}
                                onClick={() => track("challenge_submission_clicked", { challenge: ch.slug, mood_id: s.id })}
                                className="group block rounded-2xl bg-surface-1 border border-app/10 hover:border-amber-300/30 p-5 transition-colors"
                                data-testid={`challenge-submission-${s.id}`}
                            >
                                <div className="text-[10px] tracking-[0.2em] uppercase text-app-3 mb-2 flex items-center gap-2">
                                    <span>{s.pack}</span>
                                    {s.lane && <><span>·</span><span className="text-amber-300/70 normal-case tracking-normal italic font-serif">{s.lane}</span></>}
                                </div>
                                <p className="font-serif text-app text-base leading-snug line-clamp-4">
                                    {s.body || s.feeling}
                                </p>
                                <div className="mt-3 text-[10px] tracking-[0.22em] uppercase text-app-3 group-hover:text-amber-300/85 transition-colors">
                                    open card →
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
